/**
 * App-level error boundary using Sentry.
 */
import React from "react";
import * as Sentry from "@sentry/react";
import { Box, Typography, Button } from "@mui/material";

interface ErrorFallbackProps {
  error: unknown;
  resetError: () => void;
}

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  return "An unknown error occurred";
};

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, resetError }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100vh",
      p: 4,
      textAlign: "center",
    }}
  >
    <Typography variant="h4" gutterBottom>
      Something went wrong
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
      An unexpected error occurred. Our team has been notified.
    </Typography>
    <Typography variant="caption" color="text.disabled" sx={{ mb: 3 }}>
      {getErrorMessage(error)}
    </Typography>
    <Button variant="contained" onClick={resetError}>
      Try Again
    </Button>
  </Box>
);

export const AppErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Sentry.ErrorBoundary
    fallback={({ error, resetError }) => <ErrorFallback error={error} resetError={resetError} />}
    showDialog={false}
  >
    {children}
  </Sentry.ErrorBoundary>
);
